import { cn } from '@/lib/utils';
import { BotFilesMessage } from '@/types';
import { Download } from 'lucide-react';
import FileIcon from '../icons/FileIcon';

type MessageFilesProps = {
  message: BotFilesMessage;
};

function formatFileSize(size: number) {
  if (isNaN(size)) {
    return size;
  }

  if (size < 1024) {
    return `${size} B`;
  }
  return `${(size / 1024).toFixed(2)} KB`;
}

export const FileComponent = ({
  file,
  position,
  isSingleFile,
}: {
  file: BotFilesMessage['content']['files'][0];
  position: 'first' | 'middle' | 'last';
  isSingleFile: boolean;
}) => {
  return (
    <a
      href={file.url}
      target="_blank"
      download={file.url}
      className={cn(
        'group flex w-full cursor-pointer border border-border bg-[rgba(0,0,0,0)] px-[12px] py-[8px] transition-colors hover:bg-[rgba(0,0,0,0.05)]',
        {
          'rounded-t-xl': position === 'first',
          'rounded-b-xl': position === 'last',
          'rounded-none': position === 'middle',
          'rounded-xl': isSingleFile,
        }
      )}
    >
      <div className="mr-[12px]">
        <FileIcon fileType={file.metadata.type} className="text-border" />
      </div>
      <div className="flex max-w-[80%] flex-col justify-center text-widget-text-secondary">
        <span className="line-clamp-1 text-sm font-bold">{file.metadata.name}</span>
        {file.metadata.size && (
          <span className="text-sm">{formatFileSize(file.metadata.size)}</span>
        )}
      </div>
      <div className="ml-auto flex w-0 items-center opacity-0 transition-all group-hover:w-[15px] group-hover:opacity-100">
        <Download className="inline size-[15px] text-widget-text-secondary" />
      </div>
    </a>
  );
};

const MessageFiles = ({ message }: MessageFilesProps) => {
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-2 text-widget-text-secondary">
        <h3 className="text-md font-bold">
          <Download className="mr-1 inline-block size-[20px]" />
          <span>{message.content.title}</span>
        </h3>
        <p className="text-sm">{message.content.description}</p>
      </div>
      {message.content.files.map((file, index) => (
        <FileComponent
          key={index}
          file={file}
          position={
            index === 0 ? 'first' : index === message.content.files.length - 1 ? 'last' : 'middle'
          }
          isSingleFile={message.content.files.length === 1}
        />
      ))}
    </div>
  );
};

export default MessageFiles;
